import { useCallback, useRef, useState } from 'react';
import { Trans } from 'react-i18next';

import { Alert } from '@zooplus/ui-components';

import BraintreeCreditCardForm from '@components/BraintreeCreditCardForm/BraintreeCreditCardForm';
import BaseModal from '@components/Overlays/Modals/BaseModal/BaseModal';
import { useTranslation } from '@hooks/useTranslations';
import { useConfig } from '@providers/ConfigProvider';
import { useCreditcardService } from '@providers/CreditCardsMutationProvider';
import { OverlayIds, useOverlays } from '@providers/OverlaysProvider';
import { captureException } from '@services/sentryService';
import { ContainerType } from '@types-internal';
import {
  BraintreeCreditCardFormNewHandle as BraintreeCreditCardFormHandle,
  TokenizeCardResult,
} from '@types-internal/BraintreeTypes';
import { messageOfUnknownError } from '@utils/utils';

type NewCardModalProps = {
  containerType: ContainerType;
  customerType?: string;
  currency?: string;
  customerId?: string;
  sid?: string;
  siteId?: number;
};

/**
 * Modal holding the Braintree credit card form.
 */
const NewCardModal: React.FC<NewCardModalProps> = ({
  containerType,
  customerType,
  currency,
  customerId,
  sid,
  siteId,
}) => {
  const formHandle = useRef<BraintreeCreditCardFormHandle>(null);
  const [addCardError, setAddCardError] = useState<string>();
  const [addCardLoading, setAddCardLoading] = useState(false);
  const { hideOverlay } = useOverlays();
  const {
    config: { env },
  } = useConfig();
  const isLocalEnv = env === 'local';
  const { t } = useTranslation();
  const {
    saveCreditCard,
    data: { btToken = '' },
  } = useCreditcardService();

  const tokenizeAndSaveCard = async (): Promise<boolean> => {
    if (!formHandle.current) {
      captureException({ message: 'braintree_form_handle_not_found' });
      throw new Error(t('checkout.payment.creditcard.error.tokenization.retry'));
    }
    let tokenizationResult: TokenizeCardResult;
    try {
      tokenizationResult = await formHandle.current?.tokenizeCard();
    } catch (error: any) {
      captureException(error);
      throw new Error(t('checkout.payment.creditcard.error.tokenization.retry'));
    }
    return await saveCreditCard(tokenizationResult);
  };

  const handleAddCardSubmit = () => {
    setAddCardLoading(true);
    setAddCardError(undefined);

    tokenizeAndSaveCard()
      .then((result) => {
        setAddCardLoading(false);
        if (isLocalEnv && result) console.log('Should be redirected to preview page');
      })
      .catch((error: unknown) => {
        setAddCardLoading(false);
        setAddCardError(messageOfUnknownError(error));
      });
  };

  const handleFatalError = useCallback(() => {
    hideOverlay(OverlayIds.ADD_NEW_CARD);
  }, [hideOverlay]);

  return (
    <BaseModal
      modalId={OverlayIds.ADD_NEW_CARD}
      handleSubmit={handleAddCardSubmit}
      heading={t('checkout.payment.creditcard.addnew')}
      primaryButtonLabel={t('general.button.next')}
      headingId="add-card"
      closeBtnAriaLabel="Close"
      dataZta="modal-addCard"
      size="md"
      isLoading={addCardLoading}>
      <Alert>
        <Trans
          t={t}
          i18nKey="checkout.payment.creditcard.3d.secure.message"
          components={{ url: <a target="_blank" href="/info/about/psd2_summary" /> }}
        />
      </Alert>
      <BraintreeCreditCardForm
        token={btToken}
        containerType={containerType}
        error={addCardError}
        onFatalError={handleFatalError}
        allowVaulting={true}
        ref={formHandle}
        customerType={customerType}
        currency={currency}
        customerId={customerId}
        sid={sid}
        siteId={siteId}
        labelsInline
      />
    </BaseModal>
  );
};

export default NewCardModal;
